export type StepId = string;
export type FieldName = string;
type Subscriber = () => void;

/**
 * Field registry tracks which fields belong to which wizard step.
 * Step content can be rendered twice (offscreen for validation and visible for UI),
 * and React Strict Mode may double-mount in development. The same field can thus
 * be mounted multiple times concurrently. We use reference counting to ensure a field
 * is removed from the registry only when its last mounted instance unmounts.
 */
export function createFieldRegistry() {
  const stepIdToFieldNames = new Map<StepId, Set<FieldName>>();
  const fieldNameToStepId = new Map<FieldName, StepId>();
  const referenceCounts = new Map<FieldName, number>();
  const subscribers = new Set<Subscriber>();
  const notifySubscribers = () => subscribers.forEach((listener) => listener());

  return {
    add(stepId: StepId | null, fieldName: FieldName) {
      if (!stepId) {
        return;
      }
      // If this field was previously registered under a different step, remove it from the old step
      const previousStepId = fieldNameToStepId.get(fieldName);
      if (previousStepId && previousStepId !== stepId) {
        const previousSet = stepIdToFieldNames.get(previousStepId);
        previousSet?.delete(fieldName);
        if (previousSet && previousSet.size === 0) {
          stepIdToFieldNames.delete(previousStepId);
        }
      }
      // Ensure set for current step and add field
      if (!stepIdToFieldNames.has(stepId)) {
        stepIdToFieldNames.set(stepId, new Set());
      }
      stepIdToFieldNames.get(stepId)!.add(fieldName);
      fieldNameToStepId.set(fieldName, stepId);
      // Update reference count for new mounts and same-step duplicates (StrictMode),
      // but do NOT increment for cross-step moves.
      if (!previousStepId || previousStepId === stepId) {
        const newReferenceCount = (referenceCounts.get(fieldName) ?? 0) + 1;
        referenceCounts.set(fieldName, newReferenceCount);
      }

      notifySubscribers();
    },
    remove(fieldName: FieldName) {
      const newReferenceCount = (referenceCounts.get(fieldName) ?? 0) - 1;
      if (newReferenceCount <= 0) {
        referenceCounts.delete(fieldName);
        const stepId = fieldNameToStepId.get(fieldName);
        if (stepId) {
          const setForStep = stepIdToFieldNames.get(stepId);
          setForStep?.delete(fieldName);
          if (setForStep && setForStep.size === 0) {
            stepIdToFieldNames.delete(stepId);
          }
        }
        fieldNameToStepId.delete(fieldName);
      } else {
        referenceCounts.set(fieldName, newReferenceCount);
      }
      notifySubscribers();
    },
    getFields(stepId: StepId) {
      return stepIdToFieldNames.get(stepId) ?? new Set<FieldName>();
    },
    subscribe(listener: Subscriber) {
      subscribers.add(listener);
      return () => {
        subscribers.delete(listener);
      };
    },
    clear() {
      stepIdToFieldNames.clear();
      fieldNameToStepId.clear();
      referenceCounts.clear();
      notifySubscribers();
    },
  };
}

export type FieldRegistry = ReturnType<typeof createFieldRegistry>;
